// Menu de report rapide (WKF-013 §7.4) — retour PO 2026-07-07. Reporter une
// notification/tâche en UN clic via des presets (Ce soir, Demain matin…), avec
// l'heure cible affichée à droite. « Choisir une date et heure… » ouvre la
// modale existante (SnoozeDialog) pour un report précis.
//
// Deux usages :
//   • <QuickSnoozeMenu> — DropdownMenu autonome (bouton Clock du widget).
//   • <QuickSnoozeItems> — fragment d'items, à glisser dans un DropdownMenuSub
//     (menu ⋯ de la ligne de la page) ou tout DropdownMenuContent.

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { enUS, fr } from 'date-fns/locale'
import { toast } from 'sonner'
import { CalendarPlus, Clock } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuth } from '@/contexts/AuthContext'
import { notificationErrorMessage, useNotificationMutations } from '@/hooks/useNotifications'
import type { Notification } from '@/hooks/useNotifications.types'

import { canReschedule } from './calendar/taskCalendar.utils'
import { snoozePresets } from './snoozePresets'

interface QuickSnoozeSharedProps {
    notification: Notification
    entityId: string | null
    /** Ouvre la modale de report précis (date + heure). */
    onPickDate: (n: Notification) => void
    /** Callback optionnel après un report réussi (ex. fermer un menu parent). */
    onSnoozed?: () => void
}

/** Items du menu (presets + « Choisir une date… »). À placer dans un content. */
export function QuickSnoozeItems({ notification, entityId, onPickDate, onSnoozed }: QuickSnoozeSharedProps) {
    const { t, i18n } = useTranslation()
    const { hasPermission, user } = useAuth()
    const { snooze, updateTask } = useNotificationMutations(entityId)
    const locale = i18n.language.startsWith('en') ? enUS : fr
    const presets = useMemo(() => snoozePresets(new Date()), [])
    const reschedule = canReschedule(notification, {
        userId: user?.id,
        canUpdate: hasPermission('tasks:update'),
    })

    // Reporter = déplacer l'échéance (tâche) OU snoozer (alerte). Dans les deux cas,
    // même toast « Reporté au … » (le résultat visible : la ligne bouge / disparaît).
    const doPostpone = (iso: string) => {
        const handlers = {
            onSuccess: () =>
                toast.success(
                    t('notifications.snooze.doneUntil', 'Reporté au {{date}}', {
                        date: format(new Date(iso), "d MMM 'à' HH:mm", { locale }),
                    }),
                ),
            onError: (err: unknown) => toast.error(notificationErrorMessage(err, t)),
        }
        if (reschedule) {
            updateTask.mutate({ id: notification.id, payload: { due_date: iso } }, handlers)
        } else {
            snooze.mutate({ id: notification.id, snoozed_until: iso }, handlers)
        }
        onSnoozed?.()
    }

    return (
        <>
            {presets.map((p) => {
                const Icon = p.icon
                return (
                    <DropdownMenuItem key={p.key} onClick={() => doPostpone(p.iso)} className="gap-2">
                        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                        <span className="flex-1 whitespace-nowrap">{t(p.labelKey)}</span>
                        <span className="ml-auto shrink-0 whitespace-nowrap pl-3 text-xs tabular-nums text-muted-foreground">
                            {format(new Date(p.iso), 'EEE HH:mm', { locale })}
                        </span>
                    </DropdownMenuItem>
                )
            })}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onPickDate(notification)} className="gap-2">
                <CalendarPlus className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                {t('notifications.snooze.pickDateTime', 'Choisir une date et heure')}
            </DropdownMenuItem>
        </>
    )
}

interface QuickSnoozeMenuProps extends QuickSnoozeSharedProps {
    /** Libellé accessible du bouton déclencheur. */
    triggerLabel?: string
    align?: 'start' | 'end'
}

/** DropdownMenu autonome sur un bouton Clock (widget cloche). */
export function QuickSnoozeMenu({ triggerLabel, align = 'end', ...shared }: QuickSnoozeMenuProps) {
    const { t } = useTranslation()
    const label = triggerLabel ?? t('notifications.actions.snooze', 'Reporter…')
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground"
                    aria-label={label}
                >
                    <Clock className="h-4 w-4" />
                </Button>
            </DropdownMenuTrigger>
            {/* data-nested-menu : permet au panneau parent (widget cloche) de ne pas
                se fermer quand on interagit avec ce menu imbriqué (gardes hover + outside). */}
            <DropdownMenuContent align={align} className="w-72" data-nested-menu="true">
                <QuickSnoozeItems {...shared} />
            </DropdownMenuContent>
        </DropdownMenu>
    )
}
